/**
 * Which of a block's OR'd answers accept some of the same values?
 *
 * A block can offer several accepted answers at once — "10 ± 5, or 25 ± 2" — and they are meant to
 * be alternatives. When two of their bands touch, the overlap is almost always an accident of
 * tolerance rather than a decision: the teacher widened one answer and quietly swallowed another,
 * and now a submission in the shared region fires whichever answer the evaluator reaches first.
 * Nothing about the editor looks wrong, because each answer on its own is fine.
 *
 * This existed twice, as `findCompassOverlappingAnswerPairs` and `findRotationOverlappingAnswerPairs`,
 * character-for-character the same except for their interval types. Numerical and slider — which
 * have exactly the same multi-answer shape — had NEITHER, so the same authoring mistake was an error
 * on two blocks and silent on the other two.
 *
 * It takes intervals rather than a block config on purpose. Every one of the four blocks already
 * computes its own accepted ranges (each with its own rules — angle wraps a band across the 0/360
 * seam, slider clips to the track, numerical runs to infinity), and re-deriving them here would be
 * a fifth opinion about what an answer accepts. The caller brings the intervals; this only compares
 * them.
 */
const EPS = (n) => 1e-9 * Math.max(1, Math.abs(n));
/**
 * Two intervals share at least one point.
 *
 * Touching at a single endpoint counts. Both blocks that had this treated a closed band's endpoint
 * as shared, and they were right to: "0 to 10" and "10 to 20" really do both accept exactly 10, and
 * which one a student's 10 lands in is not something the teacher chose.
 */
const touches = (x, y) => x.lo <= y.hi + EPS(y.hi) && y.lo <= x.hi + EPS(x.hi);
/**
 * Every unordered pair of answers whose regions intersect, in input order.
 *
 * Pairs rather than a flat set of ids, because the message a teacher needs names both sides: "the
 * first and third answers overlap" is actionable and "three answers overlap" is not.
 */
export function overlappingAnswerPairs(regions) {
    const pairs = [];
    for (let i = 0; i < regions.length; i++) {
        for (let j = i + 1; j < regions.length; j++) {
            const a = regions[i], b = regions[j];
            if (a.intervals.some(x => b.intervals.some(y => touches(x, y))))
                pairs.push([a.id, b.id]);
        }
    }
    return pairs;
}
//# sourceMappingURL=answer-overlap.js.map